import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Eye, EyeOff, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import ImageUpload from '@/components/ImageUpload';

export default function AdminHeaderSettings() {
  const [settings, setSettings] = useState({
    logo_url: '',
    contact_button_text: 'Contact Us',
    contact_button_link: '#contact',
    navigation_links: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/admin/header');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching header settings:', error);
      toast.error('Failed to load header settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/admin/header', settings);
      toast.success('Header settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save header settings');
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const addNavigationLink = () => {
    setSettings({
      ...settings,
      navigation_links: [...settings.navigation_links, { 
        label: '', 
        url: '', 
        is_visible: true, 
        is_external: false 
      }]
    });
  };

  const updateNavigationLink = (index, field, value) => {
    const updatedLinks = [...settings.navigation_links];
    updatedLinks[index][field] = value;
    setSettings({ ...settings, navigation_links: updatedLinks });
  };

  const removeNavigationLink = (index) => {
    const updatedLinks = settings.navigation_links.filter((_, i) => i !== index);
    setSettings({ ...settings, navigation_links: updatedLinks });
  };

  const moveLink = (index, direction) => {
    const newLinks = [...settings.navigation_links];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < newLinks.length) {
      [newLinks[index], newLinks[newIndex]] = [newLinks[newIndex], newLinks[index]];
      setSettings({ ...settings, navigation_links: newLinks });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Header Settings</h1>
            <p className="text-gray-600 mt-1">Manage your website header, logo, and navigation</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
          
          <ImageUpload
            label="Header Logo"
            value={settings.logo_url}
            onChange={(url) => setSettings({ ...settings, logo_url: url })}
            required={false}
            preview={true}
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Contact Button Text</Label>
              <Input
                value={settings.contact_button_text}
                onChange={(e) => setSettings({ ...settings, contact_button_text: e.target.value })}
                placeholder="Contact Us"
              />
            </div>
            <div>
              <Label>Contact Button Link</Label>
              <Input
                value={settings.contact_button_link}
                onChange={(e) => setSettings({ ...settings, contact_button_link: e.target.value })}
                placeholder="#contact"
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-4">
              <div>
                <Label className="text-lg font-semibold">Navigation Links</Label>
                <p className="text-sm text-gray-500 mt-1">Manage header navigation menu items</p>
              </div>
              <Button onClick={addNavigationLink} size="sm" variant="outline">
                <Plus size={16} className="mr-1" />
                Add Link
              </Button>
            </div>
            
            <div className="space-y-4">
              {settings.navigation_links && settings.navigation_links.length > 0 ? (
                settings.navigation_links.map((link, index) => (
                  <div 
                    key={index} 
                    className={`p-4 border rounded-lg ${
                      link.is_visible ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Drag Handle & Order Controls */}
                      <div className="flex flex-col items-center gap-1 pt-6">
                        <GripVertical size={16} className="text-gray-400" />
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => moveLink(index, 'up')}
                            disabled={index === 0}
                            className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30"
                          >
                            ▲
                          </button>
                          <button
                            onClick={() => moveLink(index, 'down')}
                            disabled={index === settings.navigation_links.length - 1}
                            className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30"
                          >
                            ▼
                          </button>
                        </div>
                      </div>

                      {/* Form Fields */}
                      <div className="flex-1 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm">Label</Label>
                            <Input
                              value={link.label || ''}
                              onChange={(e) => updateNavigationLink(index, 'label', e.target.value)}
                              placeholder="e.g., Home"
                            />
                          </div>
                          <div>
                            <Label className="text-sm">URL</Label>
                            <Input
                              value={link.url || ''}
                              onChange={(e) => updateNavigationLink(index, 'url', e.target.value)}
                              placeholder="e.g., / or https://example.com"
                            />
                          </div>
                        </div>

                        {/* Toggle Switches */}
                        <div className="flex items-center gap-6">
                          <div className="flex items-center space-x-2">
                            <Switch
                              id={`visible-${index}`}
                              checked={link.is_visible !== false}
                              onCheckedChange={(checked) => 
                                updateNavigationLink(index, 'is_visible', checked)
                              }
                            />
                            <Label htmlFor={`visible-${index}`} className="text-sm flex items-center gap-1">
                              {link.is_visible !== false ? (
                                <>
                                  <Eye size={14} className="text-green-600" />
                                  Visible
                                </>
                              ) : (
                                <>
                                  <EyeOff size={14} className="text-gray-400" />
                                  Hidden
                                </>
                              )}
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Switch
                              id={`external-${index}`}
                              checked={link.is_external === true}
                              onCheckedChange={(checked) => 
                                updateNavigationLink(index, 'is_external', checked)
                              }
                            />
                            <Label htmlFor={`external-${index}`} className="text-sm">
                              External Link (Opens in new tab)
                            </Label>
                          </div>
                        </div>
                      </div>

                      {/* Delete Button */}
                      <Button
                        onClick={() => removeNavigationLink(index)}
                        size="sm"
                        variant="destructive"
                        className="mt-6"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No navigation links yet. Click &quot;Add Link&quot; to create one.
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save size={18} className="mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
